import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import ComicButton from "../../shared/components/ComicButton/ComicButton";

// Modal de éxito animado
const SuccessModal = ({ isVisible, onClose }) => {
  const { t } = useTranslation();

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.5, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.5, opacity: 0, y: 50 }}
            transition={{ type: "spring", damping: 15, stiffness: 300 }}
            className="bg-gradient-to-br from-gray-800 to-gray-900 p-8 rounded-2xl shadow-2xl border-2 border-yellow-500 max-w-md mx-4 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Icono animado */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="w-20 h-20 mx-auto mb-4 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center"
            >
              <motion.svg
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="w-10 h-10 text-gray-900"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <motion.path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={3}
                  d="M5 13l4 4L19 7"
                />
              </motion.svg>
            </motion.div>

            {/* Título */}
            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-2xl font-bold text-yellow-400 mb-2"
            >
              {t("opinionForm.success.title", "¡Gracias!")}
            </motion.h3>

            {/* Mensaje */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-gray-300 mb-6"
            >
              {t("opinionForm.success.message", "Tu opinión ha sido enviada correctamente. ¡Nos ayuda mucho a mejorar!")}
            </motion.p>

            {/* Partículas decorativas */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
              {[...Array(6)].map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0, x: 0, y: 0 }}
                  animate={{
                    opacity: [0, 1, 0],
                    scale: [0, 1, 0.5],
                    x: [0, (i % 2 === 0 ? 1 : -1) * (30 + i * 20)],
                    y: [0, -50 - i * 15],
                  }}
                  transition={{ delay: 0.5 + i * 0.1, duration: 1 }}
                  className="absolute left-1/2 top-1/2 w-3 h-3 rounded-full bg-yellow-400"
                  style={{ filter: "blur(1px)" }}
                />
              ))}
            </div>

            {/* Botón de cerrar */}
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onClose}
              className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-gray-900 font-bold py-3 px-8 rounded-lg hover:shadow-lg hover:shadow-yellow-500/30 transition-all"
            >
              {t("opinionForm.success.button", "Continuar")}
            </motion.button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const OpinionForm = ({ onBack }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm();

  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Obtener el valor actual de "edad" para controlar la entrada
  const edadValue = watch("edad") || "";

  // Función para manejar la entrada y permitir solo números hasta 3 dígitos
  const handleEdadChange = (e) => {
    const { value } = e.target;
    // Solo permitir números y limitar a 3 caracteres
    if (/^\d{0,3}$/.test(value)) {
      setValue("edad", value);
    }
  };

  // Obtener el valor actual de "cp" para controlar la entrada
  const cpValue = watch("cp") || "";

  // Función para manejar la entrada y permitir solo números
  const handleCpChange = (e) => {
    const { value } = e.target;
    // Solo permitir números y limitar a 5 caracteres
    if (/^\d{0,5}$/.test(value)) {
      setValue("cp", value);
    }
  };

  // Función para enviar los datos al backend
  const onSubmit = async (data) => {
    console.log("Datos enviados:", data);
    setIsSubmitting(true);
    try {
      const response = await fetch(`/api/opinions/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
      const responseData = await response.json();
      if (!response.ok) {
        console.error("Errores del servidor:", responseData.errors);
        throw new Error("Error en la solicitud");
      }
      setShowSuccess(true);
    } catch (error) {
      console.error("Error al enviar el formulario:", error);
      alert("Error en el envío del formulario.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    onBack();
  };

  return (
    <>
      <SuccessModal isVisible={showSuccess} onClose={handleSuccessClose} />

      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg w-full max-w-2xl h-5/6 overflow-hidden flex flex-col">
        <h2 className="text-2xl font-bold mb-4 text-black dark:text-white">
          <Trans i18nKey="opinionForm.title" />
        </h2>
        <div className="overflow-y-auto h-full pr-4">
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
            {/* Pregunta 1 */}
            <div className="mb-4">
              <label className="text-left block font-semibold text-gray-700 dark:text-gray-300">
                <Trans i18nKey="opinionForm.question1.label" />
              </label>
              <div className="justify-center flex gap-4">
                <label>
                  <input
                    type="radio"
                    value="Hombre"
                    {...register("sexo", { required: true })}
                  />{" "}
                  <Trans i18nKey="opinionForm.question1.option1" />
                </label>
                <label>
                  <input
                    type="radio"
                    value="Mujer"
                    {...register("sexo", { required: true })}
                  />{" "}
                  <Trans i18nKey="opinionForm.question1.option2" />
                </label>
                <label>
                  <input
                    type="radio"
                    value="Otro"
                    {...register("sexo", { required: true })}
                  />{" "}
                  <Trans i18nKey="opinionForm.question1.option3" />
                </label>
              </div>
              {errors.sexo && (
                <span className="text-red-500 text-sm">
                  <Trans i18nKey="opinionForm.question1.error" />
                </span>
              )}
            </div>

            {/* Pregunta 2 */}
            <div className="mb-4">
              <label className="text-left block font-semibold text-gray-700 dark:text-gray-300">
                <Trans i18nKey="opinionForm.question2.label" />
              </label>
              <input
                type="number"
                inputMode="numeric"
                autoComplete="age"
                min={1}
                max={999}
                step={1}
                {...register("edad", {
                  required: true,
                  min: 1,
                  max: 999,
                  pattern: /^\d{1,3}$/,
                })}
                value={edadValue}
                onChange={handleEdadChange}
                className="border rounded-md p-2 w-full"
              />
              {errors.edad && (
                <span className="text-red-500 text-sm">
                  <Trans i18nKey="opinionForm.question2.error" />
                </span>
              )}
            </div>

            {/* Pregunta 3 */}
            <div className="mb-4">
              <label className="text-left block font-semibold text-gray-700 dark:text-gray-300">
                <Trans i18nKey="opinionForm.question3.label" />
              </label>
              <input
                type="number"
                inputMode="numeric"
                autoComplete="postal-code"
                maxLength={5}
                {...register("cp", {
                  required: true,
                  pattern: /^[0-9]{5}$/,
                })}
                value={cpValue}
                onChange={handleCpChange}
                className="border rounded-md p-2 w-full"
              />
              {errors.cp && (
                <span className="text-red-500 text-sm">
                  <Trans i18nKey="opinionForm.question3.error" />
                </span>
              )}
            </div>

            {/* Pregunta 4 */}
            <div className="mb-4">
              <label className="text-left block font-semibold text-gray-700 dark:text-gray-300">
                <Trans i18nKey="opinionForm.question4.label" />
              </label>
              <div className="justify-center flex gap-4">
                <label>
                  <input
                    type="radio"
                    value="Digital"
                    {...register("formato", { required: true })}
                  />{" "}
                  <Trans i18nKey="opinionForm.question4.option1" />
                </label>
                <label>
                  <input
                    type="radio"
                    value="Impreso"
                    {...register("formato", { required: true })}
                  />{" "}
                  <Trans i18nKey="opinionForm.question4.option2" />
                </label>
              </div>
              {errors.formato && (
                <span className="text-red-500 text-sm">
                  <Trans i18nKey="opinionForm.question4.error" />
                </span>
              )}
            </div>

            {/* Pregunta 5 */}
            <div className="mb-4">
              <label className="text-left block font-semibold text-gray-700 dark:text-gray-300">
                <Trans i18nKey="opinionForm.question5.label" />
              </label>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                <Trans
                  i18nKey="opinionForm.question5.description"
                  components={{ bold: <b /> }}
                />
              </p>
              <div className="flex justify-between mt-2">
                {[1, 2, 3, 4, 5].map((value) => (
                  <label key={value} className="flex flex-col items-center">
                    <input
                      type="radio"
                      value={value}
                      {...register("lectorHabitual", { required: true })}
                      className="form-radio text-yellow-500"
                    />
                    <span className="mt-1 text-sm text-black dark:text-white">{value}</span>
                  </label>
                ))}
              </div>
              {errors.lectorHabitual && (
                <span className="text-red-500 text-left block mt-2">
                  <Trans i18nKey="opinionForm.question5.error" />
                </span>
              )}
            </div>

            {/* Pregunta 6 */}
            <div className="mb-4">
              <label className="text-left block font-semibold text-gray-700 dark:text-gray-300">
                <Trans i18nKey="opinionForm.question6.label" />
              </label>
              <div className="justify-center flex gap-4">
                <label>
                  <input
                    type="radio"
                    value="Si"
                    {...register("visitadoLugar", { required: true })}
                  />{" "}
                  <Trans i18nKey="opinionForm.question6.option1" />
                </label>
                <label>
                  <input
                    type="radio"
                    value="No"
                    {...register("visitadoLugar", { required: true })}
                  />{" "}
                  <Trans i18nKey="opinionForm.question6.option2" />
                </label>
              </div>
              {errors.visitadoLugar && (
                <span className="text-red-500 text-sm">
                  <Trans i18nKey="opinionForm.question6.error" />
                </span>
              )}
            </div>

            {/* Pregunta 7 */}
            <div className="mb-4">
              <label className="text-left block font-semibold text-gray-700 dark:text-gray-300">
                <Trans i18nKey="opinionForm.question7.label" />
              </label>
              <div className="justify-center flex gap-4">
                <label>
                  <input
                    type="radio"
                    value="Si"
                    {...register("conociaLugar", { required: true })}
                  />{" "}
                  <Trans i18nKey="opinionForm.question7.option1" />
                </label>
                <label>
                  <input
                    type="radio"
                    value="No"
                    {...register("conociaLugar", { required: true })}
                  />{" "}
                  <Trans i18nKey="opinionForm.question7.option2" />
                </label>
              </div>
              {errors.conociaLugar && (
                <span className="text-red-500 text-sm">
                  <Trans i18nKey="opinionForm.question7.error" />
                </span>
              )}
            </div>

            {/* Pregunta 8 */}
            <div className="mb-4">
              <label className="text-left block font-semibold text-gray-700 dark:text-gray-300">
                <Trans i18nKey="opinionForm.question8.label" />
              </label>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                <Trans
                  i18nKey="opinionForm.question8.description"
                  components={{ bold: <b /> }}
                />
              </p>
              <div className="flex justify-between mt-2">
                {[1, 2, 3, 4, 5].map((value) => (
                  <label key={value} className="flex flex-col items-center">
                    <input
                      type="radio"
                      value={value}
                      {...register("visitarLugar", { required: true })}
                      className="form-radio text-yellow-500"
                    />
                    <span className="mt-1 text-sm text-black dark:text-white">{value}</span>
                  </label>
                ))}
              </div>
              {errors.visitarLugar && (
                <span className="text-red-500">
                  <Trans i18nKey="opinionForm.question8.error" />
                </span>
              )}
            </div>

            {/* Agrupación de Preguntas emociones */}
            <fieldset className="mb-4 border rounded-md p-4">
              <legend className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                <Trans i18nKey="opinionForm.emotionsGroup.label" />
              </legend>

              {/* Sub-Pregunta a */}
              <div className="mt-4">
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                  <Trans
                    i18nKey="opinionForm.question9.description"
                    components={{ bold: <b /> }}
                  />
                </p>
                <div className="flex justify-between mt-2">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <label key={value} className="flex flex-col items-center">
                      <input
                        type="radio"
                        value={value}
                        {...register("emocionCuriosidad", { required: true })}
                        className="form-radio text-yellow-500"
                      />
                      <span className="mt-1 text-sm text-black dark:text-white">{value}</span>
                    </label>
                  ))}
                </div>
                {errors.emocionCuriosidad && (
                  <span className="text-red-500">
                    <Trans i18nKey="opinionForm.question9.error" />
                  </span>
                )}
              </div>

              {/* Sub-Pregunta b */}
              <div className="mt-4">
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                  <Trans
                    i18nKey="opinionForm.question10.description"
                    components={{ bold: <b /> }}
                  />
                </p>
                <div className="flex justify-between mt-2">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <label key={value} className="flex flex-col items-center">
                      <input
                        type="radio"
                        value={value}
                        {...register("emocionOrgullo", { required: true })}
                        className="form-radio text-yellow-500"
                      />
                      <span className="mt-1 text-sm text-black dark:text-white">{value}</span>
                    </label>
                  ))}
                </div>
                {errors.emocionOrgullo && (
                  <span className="text-red-500">
                    <Trans i18nKey="opinionForm.question10.error" />
                  </span>
                )}
              </div>

              {/* Sub-Pregunta c */}
              <div className="mt-4">
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                  <Trans
                    i18nKey="opinionForm.question11.description"
                    components={{ bold: <b /> }}
                  />
                </p>
                <div className="flex justify-between mt-2">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <label key={value} className="flex flex-col items-center">
                      <input
                        type="radio"
                        value={value}
                        {...register("emocionesMonumento", { required: true })}
                        className="form-radio text-yellow-500"
                      />
                      <span className="mt-1 text-sm text-black dark:text-white">{value}</span>
                    </label>
                  ))}
                </div>
                {errors.emocionesMonumento && (
                  <span className="text-red-500">
                    <Trans i18nKey="opinionForm.question11.error" />
                  </span>
                )}
              </div>
            </fieldset>

            {/* Pregunta 10 */}
            <div className="mb-4">
              <label className="text-left block font-semibold text-gray-700 dark:text-gray-300">
                <Trans i18nKey="opinionForm.question12.label" />
              </label>
              <div className="justify-center flex gap-4">
                <label>
                  <input
                    type="radio"
                    value="Corto"
                    {...register("tiempoLectura", { required: true })}
                  />{" "}
                  <Trans i18nKey="opinionForm.question12.option1" />
                </label>
                <label>
                  <input
                    type="radio"
                    value="Adecuado"
                    {...register("tiempoLectura", { required: true })}
                  />{" "}
                  <Trans i18nKey="opinionForm.question12.option2" />
                </label>
                <label>
                  <input
                    type="radio"
                    value="Largo"
                    {...register("tiempoLectura", { required: true })}
                  />{" "}
                  <Trans i18nKey="opinionForm.question12.option3" />
                </label>
              </div>
              {errors.tiempoLectura && (
                <span className="text-red-500 text-sm">
                  <Trans i18nKey="opinionForm.question12.error" />
                </span>
              )}
            </div>

            {/* Pregunta 11 */}
            <div className="mb-4">
              <label className="text-left block font-semibold text-gray-700 dark:text-gray-300">
                <Trans i18nKey="opinionForm.question13.label" />
              </label>
              <div className="justify-center flex gap-4">
                <label>
                  <input
                    type="radio"
                    value="Si"
                    {...register("flashbacksEntendidos", { required: true })}
                  />{" "}
                  <Trans i18nKey="opinionForm.question13.option1" />
                </label>
                <label>
                  <input
                    type="radio"
                    value="No"
                    {...register("flashbacksEntendidos", { required: true })}
                  />{" "}
                  <Trans i18nKey="opinionForm.question13.option2" />
                </label>
              </div>
              {errors.flashbacksEntendidos && (
                <span className="text-red-500 text-sm">
                  <Trans i18nKey="opinionForm.question13.error" />
                </span>
              )}
            </div>

            {/* Pregunta 12 */}
            <div className="mb-4">
              <label className="text-left block font-semibold text-gray-700 dark:text-gray-300">
                <Trans i18nKey="opinionForm.question14.label" />
              </label>
              <div className="justify-center flex gap-4">
                <label>
                  <input
                    type="radio"
                    value="Digital"
                    {...register("formatoPreferido", { required: true })}
                  />{" "}
                  <Trans i18nKey="opinionForm.question14.option1" />
                </label>
                <label>
                  <input
                    type="radio"
                    value="Impreso"
                    {...register("formatoPreferido", { required: true })}
                  />{" "}
                  <Trans i18nKey="opinionForm.question14.option2" />
                </label>
                <label>
                  <input
                    type="radio"
                    value="Ambos"
                    {...register("formatoPreferido", { required: true })}
                  />{" "}
                  <Trans i18nKey="opinionForm.question14.option3" />
                </label>
                <label>
                  <input
                    type="radio"
                    value="NSNC"
                    {...register("formatoPreferido", { required: true })}
                  />{" "}
                  <Trans i18nKey="opinionForm.question14.option4" />
                </label>
              </div>
              {errors.formatoPreferido && (
                <span className="text-red-500 text-sm">
                  <Trans i18nKey="opinionForm.question14.error" />
                </span>
              )}
            </div>

            {/* Pregunta 13 */}
            <div className="mb-4">
              <label className="text-left block font-semibold text-gray-700 dark:text-gray-300">
                <Trans i18nKey="opinionForm.question15.label" />
              </label>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                <Trans
                  i18nKey="opinionForm.question15.description"
                  components={{ bold: <b /> }}
                />
              </p>
              <div className="flex justify-between mt-2">
                {[1, 2, 3, 4, 5].map((value) => (
                  <label key={value} className="flex flex-col items-center">
                    <input
                      type="radio"
                      value={value}
                      {...register("interesAventuras", { required: true })}
                      className="form-radio"
                    />
                    <span className="text-xs">{value}</span>
                  </label>
                ))}
              </div>
              {errors.interesAventuras && (
                <span className="text-red-500 text-sm">
                  <Trans i18nKey="opinionForm.question15.error" />
                </span>
              )}
            </div>

            {/* Pregunta 14 */}
            <div className="mb-4">
              <label className="text-left block font-semibold text-gray-700 dark:text-gray-300">
                <Trans i18nKey="opinionForm.question16.label" />
              </label>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                <Trans
                  i18nKey="opinionForm.question16.description"
                  components={{ bold: <b /> }}
                />
              </p>
              <div className="flex justify-between mt-2">
                {[1, 2, 3, 4, 5].map((value) => (
                  <label key={value} className="flex flex-col items-center">
                    <input
                      type="radio"
                      value={value}
                      {...register("narrativaMonumento", { required: true })}
                      className="form-radio"
                    />
                    <span className="text-xs">{value}</span>
                  </label>
                ))}
              </div>
              {errors.narrativaMonumento && (
                <span className="text-red-500 text-sm">
                  <Trans i18nKey="opinionForm.question16.error" />
                </span>
              )}
            </div>

            {/* Pregunta 15 */}
            <div className="mb-4">
              <label className="text-left block font-semibold text-gray-700 dark:text-gray-300">
                <Trans i18nKey="opinionForm.question17.label" />
              </label>
              <textarea
                {...register("comentarios")}
                className="border rounded-md p-2 w-full"
              ></textarea>
            </div>

            {/* Pregunta 16 */}
            <div className="mb-4">
              <label className="text-left block font-semibold text-gray-700 dark:text-gray-300">
                <Trans i18nKey="opinionForm.chapter.label" />
              </label>
              <div className="justify-center flex gap-4">
                <label>
                  <input
                    type="radio"
                    value="Capítulo 1"
                    {...register("capitulo", { required: true })}
                  />{" "}
                  <Trans i18nKey="opinionForm.chapter.option1" />
                </label>
                <label>
                  <input
                    type="radio"
                    value="Capítulo 2"
                    {...register("capitulo", { required: true })}
                  />{" "}
                  <Trans i18nKey="opinionForm.chapter.option2" />
                </label>
                <label>
                  <input
                    type="radio"
                    value="Capítulo 3"
                    {...register("capitulo", { required: true })}
                  />{" "}
                  <Trans i18nKey="opinionForm.chapter.option3" />
                </label>
                <label>
                  <input
                    type="radio"
                    value="Capítulo 4"
                    {...register("capitulo", { required: true })}
                  />{" "}
                  <Trans i18nKey="opinionForm.chapter.option4" />
                </label>
                <label>
                  <input
                    type="radio"
                    value="Capítulo 5"
                    {...register("capitulo", { required: true })}
                  />{" "}
                  <Trans i18nKey="opinionForm.chapter.option5" />
                </label>
              </div>
              <label>
                <input
                  type="radio"
                  value="Capítulo 6"
                  {...register("capitulo", { required: true })}
                />{" "}
                <Trans i18nKey="opinionForm.chapter.option6" />
              </label>
              <label>
                <input
                  type="radio"
                  value="Capítulo 7"
                  {...register("capitulo", { required: true })}
                />{" "}
                <Trans i18nKey="opinionForm.chapter.option7" />
              </label>
              <label>
                <input
                  type="radio"
                  value="Capítulo 8"
                  {...register("capitulo", { required: true })}
                />{" "}
                <Trans i18nKey="opinionForm.chapter.option8" />
              </label>
              <label>
                <input
                  type="radio"
                  value="Capítulo 9"
                  {...register("capitulo", { required: true })}
                />{" "}
                <Trans i18nKey="opinionForm.chapter.option9" />
              </label>
              {errors.capitulo && (
                <span className="text-red-500 text-sm">
                  <Trans i18nKey="opinionForm.chapter.error" />
                </span>
              )}
            </div>

            {/* Casilla de Verificación GDPR */}
            <div className="mb-4">
              <label className="flex items-center text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  {...register("gdpr", { required: true })}
                  className="form-checkbox h-5 w-5 text-yellow-500"
                />
                <span className="ml-2 text-sm">
                  <Trans
                    i18nKey="opinionForm.gdpr.label"
                    components={[
                      <a
                        key="privacyLink"
                        href="/privacidad"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 underline"
                      >
                        Política de privacidad
                      </a>,
                    ]}
                  />
                </span>
              </label>
              {errors.gdpr && (
                <span className="text-red-500 text-sm">
                  <Trans i18nKey="opinionForm.gdpr.error" />
                </span>
              )}
            </div>

            {/* Botones */}
            <div className="flex gap-4 justify-center">
              <ComicButton
                buttonName={isSubmitting ? "Enviando..." : <Trans i18nKey="opinionForm.buttons.submit" />}
                hoverBtnName={isSubmitting ? "Enviando..." : <Trans i18nKey="opinionForm.buttons.submit" />}
                handleButtonClick={() => {
                  if (!isSubmitting) document.querySelector('form').requestSubmit();
                }}
              />
              <ComicButton
                buttonName={<Trans i18nKey="opinionForm.buttons.back" />}
                hoverBtnName={<Trans i18nKey="opinionForm.buttons.back" />}
                handleButtonClick={onBack}
              />
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default OpinionForm;